import React from 'react';
import { Box, List, ListItem, ListItemButton, ListItemText, Typography } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import { loadable } from '../utils/loadable';
import { bbox } from '@turf/turf';
import { cameraFlightAtom } from '../map/flyToFeature';
import { selectEventSnapshotAtom } from '../map/selectEventSnapshot';
import { recoveryColor, TING_LINE_ALPHA, tingOutagesSelector } from './TingOutagesLayer';

// The ramp's fourth channel is 0..255, which CSS would clamp to 1, so the row
// border takes the three colour channels plus an explicit alpha fraction.
const ROW_BORDER_ALPHA = 0.7;

export const TingOutageList = React.memo((props: { setSelectedMap: () => void }) => {
  // The 24/7 Monitoring list only exists on the home dashboard, which displays
  // these outages.
  const outages = useAtomValue(loadable(tingOutagesSelector(true)));
  const flyTo = useSetAtom(cameraFlightAtom);
  const selectEventSnapshot = useSetAtom(selectEventSnapshotAtom);

  if (outages.state === 'loading') {
    return <Typography variant="caption">Loading Ting outages...</Typography>;
  }

  if (outages.state === 'hasError') {
    return <Typography variant="caption">Ting outages failed to load</Typography>;
  }

  return (
    <Box sx={{ maxHeight: 300, overflow: 'auto', width: '100%', maxWidth: 300 }}>
      <List>
        {/* Rendered in the order the query returned them
            (structures_exposed DESC NULLS LAST). Do not sort here. */}
        {outages.data.features.map((feature, index) => {
          const {
            event_id,
            structures_exposed,
            county_intersect,
            state_intersect,
            recovered_percentage,
          } = feature.properties;
          const [r, g, b] = recoveryColor(recovered_percentage, TING_LINE_ALPHA);
          const place = [county_intersect, state_intersect]
            .filter((part): part is string => part != null)
            .join(' - ');

          return (
            <ListItem
              // A row is one polygon part, not one event: a multi-part outage
              // legitimately repeats its event_id with its own structure count.
              key={`${event_id}-${index}`}
              disablePadding
              disableGutters
              sx={{
                color: '#565c65',
                overflow: 'hidden',
                borderLeft: `6px solid rgba(${r}, ${g}, ${b}, ${ROW_BORDER_ALPHA})`,
                borderBottom: '1px solid #cfe2f0',
                '&:hover': {
                  color: '#005288',
                  backgroundColor: 'rgb(233, 240, 245)',
                },
              }}
            >
              <ListItemButton
                sx={{ overflow: 'hidden' }}
                onClick={() => {
                  props.setSelectedMap();
                  void selectEventSnapshot('ting', feature.properties);
                  flyTo({ bounds: bbox(feature) });
                }}
              >
                <ListItemText
                  slotProps={{
                    primary: {
                      sx: {
                        display: 'flex',
                        flexDirection: 'column',
                      },
                    },
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    {`${structures_exposed?.toLocaleString() ?? '--'} structures affected`}
                  </Typography>
                  {place.length > 0 && <Typography variant="caption">{place}</Typography>}
                  <Typography variant="caption">
                    {/* A missing estimate is not a zero: reading it as "0% restored"
                        would state an outage is entirely unrecovered when the vendor
                        simply did not report a figure. */}
                    {recovered_percentage == null
                      ? 'Recovery unknown'
                      : `${Math.round(recovered_percentage * 100)}% restored`}
                  </Typography>
                </ListItemText>
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
});
